// src/components/kiosk/POSSection/PaymentModal.jsx
import { formatCurrency } from '../../../utils/formatters';
import { X, CreditCard, Banknote, Building2 } from 'lucide-react';

function PaymentModal({ total, paymentMethod, onSetPaymentMethod, onConfirm, onClose, loading }) {
  const paymentMethods = [
    { id: 'cash', name: 'Cash', icon: Banknote, color: '#10b981' },
    { id: 'card', name: 'Card', icon: CreditCard, color: '#3b82f6' },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: Building2, color: '#8b5cf6' }
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        fontFamily: 'Inter, system-ui, sans-serif'
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '1rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '28rem'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
            Complete Payment
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#6b7280',
              opacity: loading ? 0.5 : 1
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Total Amount */}
          <div
            style={{
              padding: '1.5rem',
              marginBottom: '1.5rem',
              borderRadius: '0.75rem',
              background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
              textAlign: 'center'
            }}
          >
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
              Total Amount
            </p>
            <p style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {formatCurrency(total)}
            </p>
          </div>

          {/* Payment Methods */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              Select Payment Method
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              {paymentMethods.map(method => {
                const Icon = method.icon;
                const isSelected = paymentMethod === method.id;
                
                return (
                  <button
                    key={method.id}
                    onClick={() => onSetPaymentMethod(method.id)}
                    disabled={loading}
                    style={{
                      padding: '1rem',
                      border: `2px solid ${isSelected ? method.color : '#e5e7eb'}`,
                      borderRadius: '0.75rem',
                      background: isSelected ? `${method.color}15` : '#ffffff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: loading ? 0.5 : 1
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 0.5rem',
                        borderRadius: '50%',
                        background: isSelected ? method.color : '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icon size={24} style={{ color: isSelected ? '#ffffff' : method.color }} />
                    </div>
                    <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937', margin: 0, textAlign: 'center' }}>
                      {method.name}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment Instructions */}
          {paymentMethod === 'cash' && (
            <div style={{ padding: '0.75rem', borderRadius: '0.5rem', background: '#d1fae5', border: '1px solid #a7f3d0' }}>
              <p style={{ fontSize: '0.875rem', color: '#065f46', margin: 0 }}>
                💵 Collect cash payment from customer
              </p>
            </div>
          )}

          {paymentMethod === 'card' && (
            <div style={{ padding: '0.75rem', borderRadius: '0.5rem', background: '#dbeafe', border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
                💳 Process card payment using POS terminal
              </p>
            </div>
          )}

          {paymentMethod === 'bank_transfer' && (
            <div style={{ padding: '0.75rem', borderRadius: '0.5rem', background: '#e9d5ff', border: '1px solid #d8b4fe' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b21a8', margin: 0 }}>
                🏦 Verify bank transfer confirmation before completing
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '0.75rem', padding: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              background: '#ffffff',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'Inter, sans-serif',
              opacity: loading ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.5rem',
              background: loading ? '#9ca3af' : '#3b82f6',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'Inter, sans-serif',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                Processing...
              </>
            ) : (
              'Confirm Payment'
            )}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default PaymentModal;